import { useEffect, useState } from 'react';
import { ClinicInfo } from '@/types/clinic';
import { BookingApi } from '@/utils/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';

export function HomePage() {
  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDefaultClinic = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try to fetch default clinic config
        const cfg = await BookingApi.getClinicConfigBySlug('default');
        const cs = (cfg.active_theme?.theme?.color_scheme ||
          cfg.active_theme?.color_scheme) as unknown as Record<string, string>;
        const clinicData: ClinicInfo = {
          id: String(cfg.location.id),
          name: cfg.location.name,
          slug: cfg.location.slug,
          description: cfg.ai_agent?.greeting_message || '',
          address: '',
          phone: '',
          email: '',
          website: undefined,
          theme: {
            primaryColor: cs?.primary || '#2563eb',
            secondaryColor: cs?.secondary || '#1d4ed8',
            accentColor: cs?.main || cs?.primary || '#2563eb',
            backgroundColor: '#FFFFFF',
            textColor: cs?.['text-color'] || '#1F2937',
            mainColor: cs?.primary || '#2563eb',
            gradient: (cs?.gradient as string) ?? null,
            iconStroke: cs?.['icon-stroke'] || cs?.secondary || undefined,
          },
          services: [],
          doctors: [],
          workingHours: {
            monday: { isOpen: false },
            tuesday: { isOpen: false },
            wednesday: { isOpen: false },
            thursday: { isOpen: false },
            friday: { isOpen: false },
            saturday: { isOpen: false },
            sunday: { isOpen: false },
          },
          isActive: true,
          aiAgent: cfg.ai_agent,
        };
        setClinic(clinicData);
      } catch (err) {
        setError('Error loading clinic information');
        console.error('Error fetching clinic:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDefaultClinic();
  }, []);

  // Theme is initialized globally in App via ThemeInitializer

  if (loading) {
    return (
      <div className='h-screen w-screen overflow-hidden relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className='h-screen w-screen overflow-hidden relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className='h-screen w-screen overflow-hidden relative flex flex-col'
      style={{
        color: 'var(--clinic-text)',
      }}
    >
      {/* Header */}
      <header
        className='text-white py-4 flex-shrink-0'
        style={{ background: 'var(--clinic-secondary)' }}
      >
        <div className='container mx-auto px-4 text-center'>
          <h1 className='text-2xl font-bold mb-1'>{clinic.name}</h1>
          <p className='text-base opacity-90'>{clinic.description}</p>
        </div>
      </header>

      {/* Main Content */}
      <main className='container mx-auto px-4 py-4 flex-1 overflow-y-auto'>
        <div className='grid md:grid-cols-2 gap-8'>
          {/* Clinic Info */}
          <div className='bg-white rounded-lg shadow-lg p-6'>
            <h2
              className='text-2xl font-bold mb-4'
              style={{ color: 'var(--clinic-primary)' }}
            >
              Clinic Information
            </h2>
            <div className='space-y-3'>
              <p>
                <strong>Address:</strong> {clinic.address}
              </p>
              <p>
                <strong>Phone:</strong> {clinic.phone}
              </p>
              <p>
                <strong>Email:</strong> {clinic.email}
              </p>
              {clinic.website && (
                <p>
                  <strong>Website:</strong>
                  <a
                    href={clinic.website}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-600 hover:underline ml-2'
                  >
                    {clinic.website}
                  </a>
                </p>
              )}
            </div>
          </div>

          {/* Services */}
          <div className='bg-white rounded-lg shadow-lg p-6'>
            <h2
              className='text-2xl font-bold mb-4'
              style={{ color: 'var(--clinic-primary)' }}
            >
              Services
            </h2>
            <div className='space-y-3'>
              {clinic.services.map(service => (
                <div key={service.id} className='border-b pb-2'>
                  <h3 className='font-semibold'>{service.name}</h3>
                  <p className='text-gray-600 text-sm'>{service.description}</p>
                  <p className='text-sm'>
                    <span className='font-medium'>Duration:</span>{' '}
                    {service.duration} minutes -{' '}
                    <span className='font-medium'>Price:</span>{' '}
                    {service.price.toLocaleString()} Toman
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Doctors */}
        {clinic.doctors.length > 0 && (
          <div className='mt-8 bg-white rounded-lg shadow-lg p-6'>
            <h2
              className='text-2xl font-bold mb-6'
              style={{ color: 'var(--clinic-primary)' }}
            >
              Doctors
            </h2>
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {clinic.doctors.map(doctor => (
                <div key={doctor.id} className='border rounded-lg p-4'>
                  {doctor.image && (
                    <img
                      src={doctor.image}
                      alt={doctor.name}
                      className='w-full h-48 object-cover rounded-lg mb-4'
                    />
                  )}
                  <h3 className='font-bold text-lg'>{doctor.name}</h3>
                  <p className='text-gray-600 mb-2'>{doctor.specialization}</p>
                  <p className='text-sm text-gray-500 mb-2'>
                    {doctor.education}
                  </p>
                  <p className='text-sm'>{doctor.bio}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Working Hours */}
        <div className='mt-8 bg-white rounded-lg shadow-lg p-6'>
          <h2
            className='text-2xl font-bold mb-4'
            style={{ color: 'var(--clinic-primary)' }}
          >
            Working Hours
          </h2>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            {Object.entries(clinic.workingHours).map(([day, schedule]) => (
              <div key={day} className='text-center'>
                <h3 className='font-semibold capitalize'>{day}</h3>
                {schedule.isOpen ? (
                  <p className='text-sm text-green-600'>
                    {schedule.startTime} - {schedule.endTime}
                  </p>
                ) : (
                  <p className='text-sm text-red-600'>Closed</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
